import React, { useState } from 'react';
import { Spin, Alert } from 'antd';
import { useG303Query } from './hooks/useG303Query';
import QueryParams from './components/QueryParams';
import ResultTable from './components/ResultTable';
import type { QueryRequest } from '../../services/g303Api';
import './G303Form.css';

/**
 * Compute default YYYY/MM strings: from = 1 year ago, to = current month.
 */
function getDefaultYearMonth(): { from: string; to: string } {
  const now = new Date();
  const toYM = `${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}`;
  const past = new Date(now);
  past.setFullYear(past.getFullYear() - 1);
  const fromYM = `${past.getFullYear()}/${String(past.getMonth() + 1).padStart(2, '0')}`;
  return { from: fromYM, to: toYM };
}

const G303Form: React.FC = () => {
  const { loading, result, queried, noData, runQuery } = useG303Query();

  const defaults = getDefaultYearMonth();
  const [companyFrom, setCompanyFrom] = useState('');
  const [companyTo, setCompanyTo] = useState('');
  const [yearMonFrom, setYearMonFrom] = useState(defaults.from);
  const [yearMonTo, setYearMonTo] = useState(defaults.to);
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [salesFrom, setSalesFrom] = useState('');
  const [salesTo, setSalesTo] = useState('');
  const [checkDept, setCheckDept] = useState(false);
  const [checkCompany, setCheckCompany] = useState(false);

  const handleQuery = () => {
    // Determine reportType from checkboxes (BR-V05)
    let reportType = '';
    if (checkDept && checkCompany) {
      reportType = 'both';
    } else if (checkDept) {
      reportType = 'dept';
    } else if (checkCompany) {
      reportType = 'company';
    }

    const req: QueryRequest = {
      companyFrom: companyFrom.trim(),
      companyTo: companyTo.trim(),
      yearMonFrom: yearMonFrom.trim(),
      yearMonTo: yearMonTo.trim(),
      deptFrom: deptFrom.trim(),
      deptTo: deptTo.trim(),
      salesFrom: salesFrom.trim(),
      salesTo: salesTo.trim(),
      reportType,
    };
    runQuery(req);
  };

  return (
    <Spin spinning={loading}>
      <div className="g303-container max-w-7xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-4">
          應收帳款月份統計 (pwg303f1)
        </h2>

        <QueryParams
          companyFrom={companyFrom}
          companyTo={companyTo}
          yearMonFrom={yearMonFrom}
          yearMonTo={yearMonTo}
          deptFrom={deptFrom}
          deptTo={deptTo}
          salesFrom={salesFrom}
          salesTo={salesTo}
          checkDept={checkDept}
          checkCompany={checkCompany}
          loading={loading}
          onCompanyFromChange={setCompanyFrom}
          onCompanyToChange={setCompanyTo}
          onYearMonFromChange={setYearMonFrom}
          onYearMonToChange={setYearMonTo}
          onDeptFromChange={setDeptFrom}
          onDeptToChange={setDeptTo}
          onSalesFromChange={setSalesFrom}
          onSalesToChange={setSalesTo}
          onCheckDeptChange={setCheckDept}
          onCheckCompanyChange={setCheckCompany}
          onQuery={handleQuery}
        />

        {queried && !noData && result && (
          <ResultTable result={result} />
        )}

        {queried && noData && (
          <Alert
            message="查無資料"
            description="查無符合條件的應收帳款月份統計資料！請確認篩選條件是否正確，或至少勾選一種報表類型。"
            type="info"
            showIcon
          />
        )}
      </div>
    </Spin>
  );
};

export default G303Form;
